import { useId, useState, type ReactNode } from "react";
import { Check, Plus } from "lucide-react";
import { WaitlistButton } from "@/components/waitlist";
import { SignUpButton } from "@/components/auth/SignUpPrompt";

/**
 * A waitlist CTA in the marketing look. Opens the shared waitlist dialog.
 * Organization and mixed-audience CTAs only; agent CTAs use SignUpCta.
 */
export const WaitlistCta = ({
  children,
  variant = "primary",
  size = "md",
  className,
  style,
}: {
  children: ReactNode;
  variant?: "primary" | "outline" | "inverse";
  size?: "md" | "lg";
  className?: string;
  style?: React.CSSProperties;
}) => (
  <WaitlistButton
    appearance="marketing"
    className={["mk-btn", `mk-btn--${variant}`, size === "lg" && "mk-btn--lg", className].filter(Boolean).join(" ")}
    style={style}
  >
    {children}
  </WaitlistButton>
);

/**
 * An agent sign-up CTA in the marketing look (founder decision F6: open agent
 * sign-up, no waitlist). Opens AuthModal in sign-up mode; organization CTAs
 * keep WaitlistCta.
 */
export const SignUpCta = ({
  children,
  variant = "primary",
  size = "md",
  className,
  style,
}: {
  children: ReactNode;
  variant?: "primary" | "outline" | "inverse";
  size?: "md" | "lg";
  className?: string;
  style?: React.CSSProperties;
}) => (
  <SignUpButton
    appearance="marketing"
    className={["mk-btn", `mk-btn--${variant}`, size === "lg" && "mk-btn--lg", className].filter(Boolean).join(" ")}
    style={style}
  >
    {children}
  </SignUpButton>
);

/** Teal check bullets. */
export const CheckList = ({
  items,
  size = 20,
  className,
  style,
  color = "var(--mk-primary)",
}: {
  items: ReactNode[];
  size?: number;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
}) => (
  <ul className={["mk-checklist", className].filter(Boolean).join(" ")} style={style}>
    {items.map((item, i) => (
      <li key={i}>
        <Check className="mk-icon" size={size} color={color} strokeWidth={2} aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/**
 * FAQ accordion. Each answer stays in the DOM (hidden when closed) so the
 * prerendered page carries the full text for crawlers.
 */
export const Accordion = ({ items }: { items: { q: string; a: ReactNode }[] }) => {
  const base = useId();
  const [open, setOpen] = useState<Record<number, boolean>>({});
  return (
    <div className="mk-accordion">
      {items.map((item, i) => {
        const expanded = !!open[i];
        const panelId = `${base}-panel-${i}`;
        const buttonId = `${base}-button-${i}`;
        return (
          <div key={item.q} className="mk-accordion__item">
            <h3>
              <button
                type="button"
                id={buttonId}
                className="mk-accordion__btn"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => setOpen((s) => ({ ...s, [i]: !s[i] }))}
              >
                {item.q}
                <span className="mk-accordion__icon" aria-hidden="true">
                  <Plus size={22} />
                </span>
              </button>
            </h3>
            <div id={panelId} role="region" aria-labelledby={buttonId} hidden={!expanded}>
              <p className="mk-accordion__panel">{item.a}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** Full-bleed teal closing band with a centred heading and a white CTA. */
export const CtaBand = ({
  eyebrow,
  title,
  body,
  cta,
  headingLevel = "h2",
  padding,
  titleStyle,
  gap,
  ctaOffset = 8,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  cta: ReactNode;
  headingLevel?: "h2" | "h3";
  padding?: string;
  /** Per-page headline size (Home and About run larger and unconstrained). */
  titleStyle?: React.CSSProperties;
  gap?: number;
  /** Extra space above the CTA, on top of the gap. */
  ctaOffset?: number;
}) => {
  const Heading = headingLevel;
  return (
    <section className="mk-cta-band">
      <div className="mk-cta-band__inner" style={{ padding, gap }}>
        {eyebrow && <span className="mk-label mk-label--onteal">{eyebrow}</span>}
        <Heading className="mk-cta-band__title" style={titleStyle}>
          {title}
        </Heading>
        {body && <p className="mk-cta-band__body">{body}</p>}
        <div style={{ marginTop: ctaOffset, display: "flex" }}>{cta}</div>
      </div>
    </section>
  );
};

/** Home and About: display.cta at its largest, no measure cap. */
export const CTA_TITLE_LG: React.CSSProperties = {
  maxWidth: "none",
  font: "700 var(--mk-text-cta-lg)/1.04 var(--mk-sans)",
};

/** Section heading group: teal label, heading, optional paragraph. */
export const SectionHead = ({
  label,
  title,
  body,
  maxWidth = 720,
  titleClass = "mk-h2",
  as: Heading = "h2",
}: {
  label: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  maxWidth?: number;
  titleClass?: string;
  as?: "h2" | "h3";
}) => (
  <div className="mk-stack" style={{ gap: 20, maxWidth }}>
    <div className="mk-label">{label}</div>
    <Heading className={titleClass}>{title}</Heading>
    {body && <p className="mk-body-lg">{body}</p>}
  </div>
);
